import { useEffect, useState } from "react";
import { Col, Row, Tab, Tabs } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import {
  EditPropertyForm,
  ListPropertyContents,
  ListPropertyFeatures,
  ListPropertyRents,
  NewPropertyContentModalForm,
  NewPropertyFeatureForm,
  NewPropertyRentModalForm,
  PropertyContentGallery
} from "../../component";
import {
  Enumeration,
  Feature,
  PropertyContent,
  PropertyFeature,
  PropertyInfo,
  PropertyRent,
} from "../../utils/interface";
import Api from "../../Api";
import { StatusChange } from "../../container";
import { StatusFlow } from "../../common/constants/StatusFlow";
import { Config } from "../../common";
import { getFullImageUrl } from "../../utils/imageUrl";


const EditProperty = () => {
  const { propertyId } = useParams();
  const api = Api();

  const [propertyTypes, setPropertyTypes] = useState<Enumeration[]>([]);
  const [propertyInfo, setPropertyInfo] = useState<PropertyInfo>();
  const [propertyFeatures, setPropertyFeatures] = useState<PropertyFeature[]>([]);
  const [featureTypes, setFeatureTypes] = useState<Enumeration[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [propertyRents, setPropertyRents] = useState<PropertyRent[]>([]);
  const [propertyRentalTypes, setPropertyRentalTypes] = useState<Enumeration[]>([]);
  const [propertyContentTypes, setPropertyContentTypes] = useState<Enumeration[]>([]);
  const [propertyContents, setPropertyContents] = useState<PropertyContent[]>([]);
  const [images, setImages] = useState<{ original: string; thumbnail: string; }[]>([]);

  const PropertyFeatureValidationSchema = Yup.object().shape({
    propertyFeatureId: Yup.number().required('Feature is required'),
  });
  const PropertyRentValidationSchema = Yup.object().shape({
    rentalTypeEnumId: Yup.number().required('Rental Type is required'),
    rentAmount: Yup.number()
      .required('Rent Amount is required')
      .positive('Rent Amount must be a positive number')
      .integer('Rent Amount must be an integer'),
  });
  const PropertyContentValidationSchema = Yup.object().shape({
    propertyContentTypeEnumId: Yup.number().required('Property Content Type is required'),
    contentFiles: Yup.array()
      .of(
        Yup.mixed().test({
          name: 'fileValidation',
          message: 'Invalid file',
          test: (value: any) => {
            if (!value) return false;
            if (!(value instanceof File)) return false; // Ensure it's a File object
            // if (value.size > 1024 * 1024 * 2) return false; // 2MB size limit (uncomment if needed)
            if (!['image/jpeg', 'image/png', 'image/jpg'].includes(value.type)) return false;
            return true;
          },
        })
      )
      .min(1, 'At least one file is required')
      .max(5, 'You can upload a maximum of 5 files')
      .required('Content Files are required'),
  });


  const getPropertyTypes = () => {
    api.property.getPropertyTypes()
      .then((response: any) => {
        const data: Enumeration[] = response.result.data;
        setPropertyTypes(data);
      })
  };
  const getPropertyInfo = (propertyId: number) => {
    api.property.getPropertyInfo(propertyId)
      .then((response: any) => {
        setPropertyInfo(response.result.data);
      })
  };
  const savePropertyLocation = (propertyId: number, propertyLocationValues: any) => {
    api.property.savePropertyLocation(propertyId, propertyLocationValues)
    .then((response: any) => {
      toast.success(response.result.message);
    })
  };
  const handlePropertyUpdate = (propertyDetails: PropertyInfo) => {
    api.property.updateProperty(propertyDetails.propertyId, propertyDetails)
      .then((response: any) => {
        toast.success(response.result.message)
        savePropertyLocation(propertyDetails.propertyId, propertyDetails.propertyLocation);
      })
  };
  const getPropertyFeatures = (propertyId: number) => {
    api.property.getPropertyFeatures(propertyId)
      .then((response: any) => { setPropertyFeatures(response.result.data) })
  };
  const getFeatureTypes = () => {
    api.property.getFeatureTypes()
      .then((response: any) => {
        setFeatureTypes(response.result.data);
      })
  };
  const getFeatures = (propertyFeatureTypeEnumId: number) => {
    api.property.getFeatures(propertyFeatureTypeEnumId)
      .then((response: any) => { setFeatures(response.result.data) })
  }
  const onSelectFeatureType = (propertyFeatureTypeEnumId: number) => {
    getFeatures(propertyFeatureTypeEnumId);
  };
  const createPropertyFeature = (propertyFeatureInfo: any) => {
    if (propertyId) {
      const parsedPropertyId = parseInt(propertyId);
      api.property.createPropertyFeature(parsedPropertyId, propertyFeatureInfo)
        .then((response: any) => {
          toast.success(response.result.message)
          getPropertyFeatures(parsedPropertyId);
        })
    }
  };
  const deletePropertyFeature = (propertyId: number, propertyFeatureId: number, fromDate: string) => {
    api.property.deletePropertyFeature(propertyId, propertyFeatureId, fromDate)
      .then((response: any) => {
        toast.success(response.result.message);
        getPropertyFeatures(propertyId);
      })
  }
  const getPropertyRents = (propertyId: number) => {
    api.property.getPropertyRents(propertyId)
      .then((response: any) => {
        setPropertyRents(response.result.data);
      })
  };
  const deletePropertyRent = (propertyRentId: number) => {
    api.property.deletePropertyRent(propertyRentId)
      .then((response: any) => {
        toast.success(response.result.message);
        if (propertyId) {
          getPropertyRents(parseInt(propertyId));
        }
      })
  };
  const getPropertyRentalTypes = () => {
    api.property.getPropertyRentalTypes()
      .then((response: any) => {
        setPropertyRentalTypes(response.result.data);
      })
  };
  const createPropertyRent = (propertyRentInfo: any) => {
    if (propertyId) {
      const parsedPropertyId = parseInt(propertyId);
      api.property.createPropertyRent(parsedPropertyId, propertyRentInfo)
        .then((response: any) => {
          toast.success(response.result.message);
          getPropertyRents(parsedPropertyId);
        })
    }
  }
  const updatePropertyStatus = (statusId: number) => {
    if (propertyInfo) {
      const updatePropertyInfo: PropertyInfo = { ...propertyInfo, statusId };
      setPropertyInfo(updatePropertyInfo);
      handlePropertyUpdate(updatePropertyInfo);
    }
  };
  const getPropertyContentTypes = () => {
    api.property.getPropertyContentTypes()
      .then((response: any) => {
        setPropertyContentTypes(response.result.data);
      })
  };
  const bulkCreatePropertyContents = (propertyContent: any) => {
    console.log(propertyContent);
    const formData = new FormData();
    formData.append('propertyContentTypeEnumId', propertyContent.propertyContentTypeEnumId);

    propertyContent.contentFiles.forEach((contentFile: any) => {
      formData.append(`propertyContentFile`, contentFile, contentFile.name);
    });

    if (propertyId) {
      api.property.bulkCreatePropertyContents(parseInt(propertyId), formData)
        .then((response: any) => {
          toast.success(response.result.message);
          getPropertyContents(parseInt(propertyId))
        });
    }
  };
  const getPropertyContents = (propertyId: number) => {
    api.property.getPropertyContents(propertyId)
      .then((response: any) => {
        setPropertyContents(response.result.data);
        const contentImages = response.result.data.map((it: any) => {
          const imgUrl = getFullImageUrl(it.contentLocation);
          return {
            original: imgUrl,
            thumbnail: imgUrl
          };
        });
        setImages(contentImages);
      })
  };
  const deletePropertyContent = (propertyContentId: number) => {
    api.property.deletePropertyContent(propertyContentId)
      .then((response: any) => {
        toast.success(response.result.message);
        if (propertyId) {
          getPropertyContents(parseInt(propertyId));
        }
      })
  };
  const updatePropertyContent = (propertyContent: any) => {
    api.property.updatePropertyContent(propertyContent.propertyContentId, propertyContent)
      .then((response: any) => {
        toast.success(response.result.message);
        if (propertyId) {
          getPropertyContents(parseInt(propertyId));
        }
      })
  };

  useEffect(() => {
    getPropertyTypes();
    getFeatureTypes();
    getPropertyContentTypes();
    getPropertyRentalTypes();
  }, [])
  useEffect(() => {
    if (propertyId) {
      const parsedPropertyId = parseInt(propertyId);
      getPropertyContents(parsedPropertyId);
      getPropertyInfo(parsedPropertyId);
      getPropertyFeatures(parsedPropertyId);
      getPropertyRents(parsedPropertyId);
    }
  }, [propertyId])

  return (<>
    <Tabs
      defaultActiveKey="editProperty"
      id="edit-deal"
      className="mb-3"
    >
      <Tab eventKey="editProperty" title="Edit Property">
        <Row>
          <Col sm="8">
            {propertyInfo && (
              <EditPropertyForm
                propertyTypes={propertyTypes}
                propertyInfo={propertyInfo}
                onUpdatePropertyInfo={handlePropertyUpdate}
              />
            )}
          </Col>
          <Col sm="4">
            {propertyInfo && (
              <StatusChange
                statusFlowId={StatusFlow.Property}
                statusId={propertyInfo.statusId}
                handleUpdateStatus={updatePropertyStatus}
              />
            )}
          </Col>
        </Row>
      </Tab>
      <Tab eventKey="propertyRent" title="Rent">
        <NewPropertyRentModalForm
          PropertyRentValidationSchema={PropertyRentValidationSchema}
          propertyRentalTypes={propertyRentalTypes}
          onCreatePropertyRent={createPropertyRent}
        />
        <ListPropertyRents
          propertyRents={propertyRents}
          onDeletePropertyRent={deletePropertyRent}
        />
      </Tab>
      <Tab eventKey="propertyFeature" title="Feature">
        <NewPropertyFeatureForm
          featureTypes={featureTypes}
          onSelectFeatureType={onSelectFeatureType}
          features={features}
          PropertyFeatureValidationSchema={PropertyFeatureValidationSchema}
          onCreatePropertyFeature={createPropertyFeature}
        />
        <ListPropertyFeatures
          propertyFeatures={propertyFeatures}
          onDeletePropertyFeature={deletePropertyFeature}
        />
      </Tab>
      <Tab eventKey="propertyContent" title="Content">
        <PropertyContentGallery images={images} />
        <br />
        <NewPropertyContentModalForm
          propertyContentTypes={propertyContentTypes}
          PropertyContentValidationSchema={PropertyContentValidationSchema}
          onAddPropertyContent={bulkCreatePropertyContents}
        />
        <ListPropertyContents
          propertyContentTypes={propertyContentTypes}
          propertyContents={propertyContents}
          onDeletePropertyContent={deletePropertyContent}
          onUpdatePropertyContent={updatePropertyContent}
        />
      </Tab>
    </Tabs>
  </>);
};

export default EditProperty;
